<template>
  <div>
    <ClinicPageHeader
      title="Financeiro da Clínica"
      subtitle="Operação financeira — faturamento, recebimentos e repasses aos profissionais de saúde"
    >
      <template #actions>
        <select
          v-model="month"
          class="select"
          data-test="month"
          style="width:180px;height:40px"
          @change="onMonthChange"
        >
          <option v-for="m in months" :key="m.value" :value="m.value">
            {{ m.label }}
          </option>
        </select>
      </template>
    </ClinicPageHeader>

    <div class="dbody">
      <div class="tabs" style="margin-bottom:var(--space-6)">
        <router-link
          class="tab"
          active-class="tab--active"
          exact-path
          :to="{ name: 'ClinicFinanceDashboard', query: monthQuery }"
        >
          Visão geral
        </router-link>
        <router-link
          class="tab"
          active-class="tab--active"
          exact-path
          :to="{ name: 'ClinicFinanceReceivables', query: monthQuery }"
        >
          Recebimentos
        </router-link>
        <router-link
          class="tab"
          active-class="tab--active"
          exact-path
          :to="{ name: 'ClinicFinanceTransfers', query: monthQuery }"
        >
          Repasses
        </router-link>
        <router-link
          class="tab"
          active-class="tab--active"
          exact-path
          :to="{ name: 'ClinicFinanceStatement', query: monthQuery }"
        >
          Extrato
        </router-link>
      </div>

      <!-- Erro -->
      <ClinicStateBox
        v-if="state === 'error'"
        state="error"
        title="Não foi possível carregar o extrato"
        message="Ocorreu um erro ao buscar as movimentações da clínica. Tente novamente."
      >
        <template #actions>
          <button class="btn btn--primary btn--sm" @click="fetch">
            Tentar de novo
          </button>
        </template>
      </ClinicStateBox>

      <!-- Loading -->
      <!-- 403: sem acesso ao Financeiro nesta clínica (US-C11) -->
      <ClinicStateBox
        v-else-if="state === 'forbidden'"
        state="empty"
        title="Acesso restrito"
        message="Você não tem acesso ao Financeiro desta clínica. Fale com o administrador."
      />

      <template v-else-if="state === 'loading'">
        <div class="card" style="margin-bottom:var(--space-5)">
          <div class="skel-line" style="width:40%;margin-bottom:16px" />
          <div class="skel-line" style="width:60%;height:28px" />
        </div>
        <div class="card">
          <div class="skel-line" style="width:100%;height:200px" />
        </div>
      </template>

      <!-- Conteúdo -->
      <template v-else>
        <div class="dtoolbar">
          <div class="dsearch">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle
                cx="11"
                cy="11"
                r="7"
                stroke="currentColor"
                stroke-width="2"
              />
              <path
                d="M21 21l-4-4"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
            <input
              :value="filters.q"
              class="input"
              placeholder="Buscar movimentação…"
              @input="onFilter('q', $event.target.value)"
            />
          </div>
          <select
            :value="filters.type"
            class="select"
            @change="onFilter('type', $event.target.value)"
          >
            <option value="">Todos os tipos</option>
            <option value="entrada">Entradas</option>
            <option value="saida">Saídas</option>
          </select>
        </div>

        <div
          class="card"
          style="display:flex;gap:var(--space-8);margin-bottom:var(--space-5)"
        >
          <div class="stack" style="gap:2px">
            <span class="fieldset-title" style="margin:0">Saldo atual</span>
            <span class="money" style="font-size:var(--font-size-2xl)">
              {{ formatMoney(balanceCents) }}
            </span>
          </div>
          <div class="stack" style="gap:2px">
            <span class="fieldset-title" style="margin:0">Entradas (mês)</span>
            <span
              class="money money--pos"
              style="font-size:var(--font-size-2xl)"
            >
              {{ kpiMoney(inflowCents) }}
            </span>
          </div>
          <div class="stack" style="gap:2px">
            <span class="fieldset-title" style="margin:0">Saídas (mês)</span>
            <span
              class="money"
              style="font-size:var(--font-size-2xl);color:var(--color-danger)"
            >
              {{ kpiMoney(outflowCents) }}
            </span>
          </div>
        </div>

        <div class="card card--flush">
          <table v-if="entries.length" class="table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Descrição</th>
                <th>Tipo</th>
                <th class="t-right">Valor</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="e in entries" :key="e.id">
                <td class="num">{{ brDate(e.date) }}</td>
                <td class="cell-strong">{{ e.description }}</td>
                <td>
                  <span
                    class="badge badge--no-dot"
                    :class="
                      e.type === 'entrada' ? 'badge--success' : 'badge--danger'
                    "
                  >
                    {{ e.type === "entrada" ? "Entrada" : "Saída" }}
                  </span>
                </td>
                <td
                  class="num t-right money"
                  :class="e.type === 'entrada' ? 'money--pos' : ''"
                  :style="
                    e.type === 'entrada'
                      ? 'color:var(--color-success)'
                      : 'color:var(--color-danger)'
                  "
                >
                  {{ e.type === "entrada" ? "+ " : "− "
                  }}{{ formatMoney(e.amountCents) }}
                </td>
              </tr>
            </tbody>
          </table>

          <ClinicStateBox
            v-else
            state="empty"
            title="Nenhuma movimentação encontrada"
            message="Ajuste os filtros para ver as movimentações da clínica."
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import ClinicPageHeader from "@/components/clinica/ClinicPageHeader.vue";
import ClinicStateBox from "@/components/clinica/ClinicStateBox.vue";
import clinicaListQuery from "@/mixins/clinicaListQuery";
import { ClinicFinance } from "@/services/clinica";
import { formatMoney } from "@/data/clinica";
import clinicFinanceMonth from "@/mixins/clinicFinanceMonth";

export default {
  name: "ClinicFinanceStatement",
  components: { ClinicPageHeader, ClinicStateBox },
  mixins: [clinicaListQuery, clinicFinanceMonth],
  data() {
    return {
      // loading → ready/error. Preview de erro via ?state=error.
      state: "loading",
      // Filtros refletidos na URL (q, type: entrada|saida).
      filters: { q: "", type: "" },
      entries: [],
      // Snapshot completo (sem filtro) p/ totais de entradas/saídas do mês.
      allEntries: [],
      metaLoaded: false,
      balanceCents: 0
    };
  },
  created() {
    this.filters = this.clinicaReadQuery({ q: "", type: "" });
    this.fetch();
  },
  methods: {
    formatMoney,
    kpiMoney(cents) {
      return ((cents || 0) / 100).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    },
    // Entradas/saídas do topo vêm do mês inteiro: troca o mês, recarrega.
    onMonthChanged() {
      this.metaLoaded = false;
      this.fetch();
    },
    async fetch() {
      this.state = "loading";
      try {
        const fail = this.$route.query.state === "error";
        const { data } = await ClinicFinance.statement({
          month: this.month,
          q: this.filters.q,
          type: this.filters.type,
          fail
        });
        this.entries = data.entries;
        this.balanceCents = data.balanceCents;
        if (!this.metaLoaded) {
          const all = await ClinicFinance.statement({ month: this.month });
          this.allEntries = all.data.entries;
          this.metaLoaded = true;
        }
        this.state = "ready";
      } catch (e) {
        // 403 = RBAC (papel Atendimento) — não é falha de carregamento
        this.state =
          e && e.response && e.response.status === 403 ? "forbidden" : "error";
      }
    },
    onFilter(key, value) {
      this.filters = { ...this.filters, [key]: value };
      this.clinicaSyncQuery({ [key]: value });
    },
    brDate(iso) {
      const [y, m, d] = String(iso).split("-");
      return `${d}/${m}/${y}`;
    }
  },
  computed: {
    inflowCents() {
      return this.allEntries
        .filter(e => e.type === "entrada")
        .reduce((sum, e) => sum + e.amountCents, 0);
    },
    outflowCents() {
      return this.allEntries
        .filter(e => e.type === "saida")
        .reduce((sum, e) => sum + e.amountCents, 0);
    }
  },
  watch: {
    "$route.query"() {
      this.filters = this.clinicaReadQuery({ q: "", type: "" });
      this.fetch();
    }
  }
};
</script>
